import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTheme } from "../context/ThemeContext";
import {
  useLanguage,
  getLang,
  getLocale,
  getCurrencySymbol
} from "../lang/useLanguage";
import PaginationBar from "../components/PaginationBar";
import useContentPermissions from "../hooks/useContentPermissions";

const API = (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") ? "http://localhost:8001" : `${window.location.protocol}//${window.location.hostname}/api`;
const token = () => localStorage.getItem("token");
const headers = () => ({ Authorization: `Bearer ${token()}`, "Content-Type": "application/json" });
const fmt = (d) =>
  d
    ? new Date(d).toLocaleDateString(getLocale(), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      })
    : "—";

const getStatusCfg = () => {
  const l = getLang();
  const w = l.workorders || {};
  return {
    draft:       { label: w.status?.draft,     color: "#06b6d4", bg: "rgba(148,163,184,0.12)", step: 0 },
    planned:     { label: w.status?.planned,  color: "#60a5fa", bg: "rgba(96,165,250,0.12)",  step: 1 },
    released:    { label: w.status?.released,    color: "#a78bfa", bg: "rgba(167,139,250,0.12)", step: 2 },
    in_progress: { label: w.status?.in_progress,   color: "#06b6d4", bg: "rgba(6,182,212,0.12)",  step: 3 },
    completed:   { label: w.status?.completed, color: "#00ff9d", bg: "rgba(0,255,157,0.12)",   step: 4 },
    closed:      { label: w.status?.closed,  color: "#475569", bg: "rgba(71,85,105,0.12)",   step: 5 },
    cancelled:   { label: w.status?.cancelled,      color: "#f87171", bg: "rgba(248,113,113,0.12)", step: -1 },
    qc_pending:       { label: w.status?.qc_pending, color: "#f97316", bg: "rgba(249,115,22,0.12)",   step: 3.5 },
    qc_passed:        { label: w.status?.qc_passed,    color: "#10b981", bg: "rgba(16,185,129,0.12)",   step: 4   },
    final_qc_pending: { label: w.status?.final_qc_pending,     color: "#8b5cf6", bg: "rgba(139,92,246,0.12)",   step: 5.5 },
    final_qc_passed:  { label: w.status?.final_qc_passed,        color: "#10b981", bg: "rgba(16,185,129,0.12)",   step: 5.8 },
    final_qc_failed:  { label: w.status?.final_qc_failed,        color: "#ef4444", bg: "rgba(239,68,68,0.12)",    step: 5.8 },
    assembly_pending:     { label: w.status?.assembly_pending,   color: "#f59e0b", bg: "rgba(245,158,11,0.12)",   step: 4.5 },
    assembly_in_progress: { label: w.status?.assembly_in_progress,          color: "#3b82f6", bg: "rgba(59,130,246,0.12)",    step: 4.7 },
    assembly_done:    { label: w.status?.assembly_done, color: "#8b5cf6", bg: "rgba(139,92,246,0.12)",   step: 5   },
  };
};

const LIGHT_STATUS = {
  draft:       { color: "#64748b", bg: "#f8fafc" },
  planned:     { color: "#2563eb", bg: "#eff6ff" },
  released:    { color: "#7c3aed", bg: "#f5f3ff" },
  in_progress: { color: "#d97706", bg: "#fffbeb" },
  qc_pending:  { color: "#ea580c", bg: "#fff7ed" },
  completed:   { color: "#059669", bg: "#ecfdf5" },
  closed:      { color: "#475569", bg: "#f1f5f9" },
  cancelled:   { color: "#dc2626", bg: "#fef2f2" },
};

const getPriorityCfg = () => {
  const l = getLang();
  const w = l.workorders || {};
  return {
    low:      { label: w.priority?.low,      color: "#64748b" },
    normal:   { label: w.priority?.normal,   color: "#60a5fa" },
    high:     { label: w.priority?.high,     color: "#06b6d4" },
    urgent:   { label: w.priority?.urgent,   color: "#f87171" },
    critical: { label: w.priority?.critical, color: "#f87171" },
  };
};

const sc = (status, themeName) => {
  const STATUS_CFG = getStatusCfg();
  return themeName === "light"
    ? (LIGHT_STATUS[status] || LIGHT_STATUS.draft)
    : (STATUS_CFG[status] || STATUS_CFG.draft);
};


const approvalLabel = (v, status) => {
  const l = getLang();
  const w = l.workorders || {};

  if (v === "approved") return w.approvalApproved;
  if (v === "rejected") return w.approvalRejected;

  if ([
    "in_progress",
    "qc_pending",
    "qc_passed",
    "assembly_pending",
    "assembly_in_progress",
    "assembly_done",
    "assembly_completed",
    "final_qc_pending",
    "final_qc_passed",
    "completed",
    "closed"
  ].includes(status)) {
    return w.approvalApproved;
  }

  return w.approvalPending;
};

const Badge = ({ status, themeName, parentOrderNo }) => {
  const l = getLang();
  const w = l.workorders || {};
  const STATUS_CFG = getStatusCfg();
  const c = sc(status, themeName);
  const cfg = { ...(STATUS_CFG[status] || STATUS_CFG.draft) };

  if (status === "qc_pending") {
    cfg.label = parentOrderNo ? w.internalQc : w.finalQcWaiting;
  }
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: c.bg, color: c.color, border: `1px solid ${c.color}44`,
      letterSpacing: "0.04em", textTransform: "uppercase", whiteSpace: "nowrap",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.color, flexShrink: 0 }} />
      {cfg.label}
    </span>
  );
};

const PriorityBadge = ({ priority }) => {
  const PRIORITY_CFG = getPriorityCfg();
  const cfg = PRIORITY_CFG[priority] || PRIORITY_CFG.normal;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
      color: cfg.color, background: `${cfg.color}18`, border: `1px solid ${cfg.color}33`,
    }}>{cfg.label}</span>
  );
};


const depColor = (status) => status === "ready" ? "#10b981" : "#ef4444";

const depLabel = (status) => {
  const l = getLang();
  const w = l.workorders || {};
  const v = status || "ready";

  if (v === "ready") return w.dependencyReady;
  if (v === "waiting_children") return w.dependencyWaitingChildren;
  if (v === "blocked") return w.dependencyBlocked;

  return w.dependencyPending;
};

const WorkOrderTreeModal = ({ tree, loading, error, onClose, t, themeName }) => {
  const l = getLang();
  const w = l.workorders || {};

  const TreeNode = ({ node, depth = 0 }) => {
    const children = node.children || [];
    const hasChildren = children.length > 0;

    return (
      <div style={{ marginLeft: depth === 0 ? 0 : 28, position: "relative" }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 12px",
          marginBottom: 8,
          borderRadius: 12,
          background: depth === 0 ? "rgba(16,185,129,0.12)" : t.bg.metricCard,
          border: `1px solid ${depth === 0 ? "rgba(16,185,129,0.35)" : t.border.default}`,
          borderLeft: `4px solid ${hasChildren ? "#f59e0b" : depColor(node.dependency_status)}`,
        }}>
          <span style={{ fontSize: 18 }}>{hasChildren ? "📦" : "🔩"}</span>

          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <strong style={{ color: t.text.primary, fontSize: 14 }}>
                {node.product_code || node.product_name || w.treeProductFallback}
              </strong>
              <span style={{ color: t.text.muted, fontSize: 12 }}>
                {node.order_no}
              </span>
              <span style={{ color: t.text.muted, fontSize: 11 }}>
                L{node.level_no ?? depth}
              </span>
            </div>

            <div style={{ marginTop: 4, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <Badge status={node.status} themeName={themeName} parentOrderNo={node.parent_order_no} />

              <span style={{
                padding: "3px 8px",
                borderRadius: 8,
                fontSize: 11,
                fontWeight: 700,
                color: depColor(node.dependency_status),
                background: node.dependency_status === "ready"
                  ? "rgba(16,185,129,0.12)"
                  : "rgba(239,68,68,0.12)"
              }}>
                {depLabel(node.dependency_status)}
              </span>

              <span style={{ color: t.text.secondary, fontSize: 12 }}>
                {node.quantity || 0} {w.pcs}
              </span>
            </div>
          </div>
        </div>

        {children.map(child => (
          <TreeNode key={child.id} node={child} depth={depth + 1} />
        ))}
      </div>
    );
  };

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.65)",
      zIndex: 9999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 24
    }}>
      <div style={{
        width: "min(900px, 96vw)",
        maxHeight: "88vh",
        overflow: "auto",
        background: t.bg.card,
        border: `1px solid ${t.border.card}`,
        borderRadius: 18,
        boxShadow: "0 20px 60px rgba(0,0,0,0.35)"
      }}>
        <div style={{
          padding: "18px 22px",
          borderBottom: `1px solid ${t.border.subtle}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, color: t.text.primary }}>🌳 {w.treeTitle}</h2>
            <p style={{ margin: "4px 0 0", color: t.text.muted, fontSize: 13 }}>
              MRP Explosion + Dependency görünümü
            </p>
          </div>

          <button onClick={onClose} style={{
            border: "none",
            background: t.bg.toggle,
            color: t.text.primary,
            borderRadius: 10,
            padding: "8px 12px",
            cursor: "pointer",
            fontWeight: 700
          }}>
            ✕
          </button>
        </div>

        <div style={{ padding: 22 }}>
          {loading ? (
            <div style={{ color: t.text.muted, padding: 30, textAlign: "center" }}>{w.treeLoading}</div>
          ) : error ? (
            <div style={{ color: "#ef4444", padding: 20, background: "rgba(239,68,68,0.12)", borderRadius: 12 }}>
              {error}
            </div>
          ) : tree ? (
            <TreeNode node={tree} />
          ) : (
            <div style={{ color: t.text.muted }}>{w.treeNoData}</div>
          )}
        </div>
      </div>
    </div>
  );
};


// ─── MALİYET SEKMESİ ─────────────────────────────────────────────────────
const CostTab = ({ order, t, onRefresh }) => {
  const { t: l } = useLanguage();
  const w = l.workorders || {};
  const [form, setForm] = useState({
    actual_material_cost: order.actual_material_cost || "",
    actual_labor_cost:    order.actual_labor_cost    || "",
    actual_overhead_cost: order.actual_overhead_cost || "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const qty       = parseFloat(order.quantity) || 0;

  const stdMatUnit  = parseFloat(order.standard_material_cost) || 0;
  const stdLabUnit  = parseFloat(order.standard_labor_cost) || 0;
  const stdOverUnit = parseFloat(order.standard_overhead_cost) || 0;

  const stdMat    = stdMatUnit * qty;
  const stdLab    = stdLabUnit * qty;
  const stdOver   = stdOverUnit * qty;
  const stdTotal  = (parseFloat(order.unit_cost) || 0) * qty;

  const actMat    = parseFloat(form.actual_material_cost)  || 0;
  const actLab    = parseFloat(form.actual_labor_cost)     || 0;
  const actOver   = parseFloat(form.actual_overhead_cost)  || 0;
  const actTotal  = actMat + actLab + actOver;

  const variance  = stdTotal - actTotal;
  const varPct    = stdTotal > 0 ? ((variance / stdTotal) * 100).toFixed(1) : 0;

  const scrapCost = parseFloat(order.scrap_cost) || 0;
  const money = v =>
    `${Number(v || 0).toLocaleString(getLocale(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${getCurrencySymbol()}`;

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API}/work-orders/${order.id}/actual-costs`, {
        method: "PATCH", headers: headers(),
        body: JSON.stringify({
          actual_material_cost:  parseFloat(form.actual_material_cost)  || 0,
          actual_labor_cost:     parseFloat(form.actual_labor_cost)     || 0,
          actual_overhead_cost:  parseFloat(form.actual_overhead_cost)  || 0,
        })
      });
      if (res.ok) {
        setMsg({ type: "success", text: `✅ ${w.costSaved}` });
        onRefresh();
      } else {
        const d = await res.json();
        setMsg({ type: "error", text: `${w.error || "Hata"}: ` + d.detail });
      }
    } catch { setMsg({ type: "error", text: w.connectionError }); }
    setSaving(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
        {[
          { label: w.stdCost, value: stdTotal, color: t.text.secondary, icon: "📋" },
          { label: w.actCost,    value: actTotal, color: actTotal > stdTotal ? t.accent.red : t.accent.green, icon: "💸" },
          { label: w.variance   || "Varyans",          value: variance, color: variance >= 0 ? t.accent.green : t.accent.red, icon: variance >= 0 ? "✅" : "⚠️", prefix: variance >= 0 ? "+" : "" },
        ].map(card => (
          <div key={card.label} style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 12, padding: "14px 16px", textAlign: "center" }}>
            <div style={{ fontSize: 18, marginBottom: 4 }}>{card.icon}</div>
            <div style={{ fontSize: 11, color: t.text.muted, textTransform: "uppercase", marginBottom: 6 }}>{card.label}</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: card.color }}>{card.prefix || ""}{money(card.value)}</div>
            {card.label === (w.variance) && stdTotal > 0 && (
              <div style={{ fontSize: 11, color: card.color, marginTop: 4, fontWeight: 600 }}>
                {variance >= 0 ? (w.saving) : (w.overrun)} %{Math.abs(varPct)}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 14 }}>
          📊 {w.stdVsAct}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: t.bg.theadRow }}>
              {[w.item, w.standard, w.actual, w.variance, w.statusLabel].map(h => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              { label: w.material   || "Malzeme",     std: stdMat,  act: actMat  },
              { label: w.labor,     std: stdLab,  act: actLab  },
              { label: w.overhead, std: stdOver, act: actOver },
            ].map(row => {
              const v = row.std - row.act;
              return (
                <tr key={row.label} style={{ borderTop: `1px solid ${t.border.subtle}` }}>
                  <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, color: t.text.primary }}>{row.label}</td>
                  <td style={{ padding: "10px 12px", fontSize: 13, color: t.text.secondary }}>{money(row.std)}</td>
                  <td style={{ padding: "10px 12px", fontSize: 13, color: row.act > row.std ? t.accent.red : t.text.secondary }}>{row.act > 0 ? money(row.act) : "—"}</td>
                  <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: v >= 0 ? t.accent.green : t.accent.red }}>{row.act > 0 ? (v >= 0 ? "+" : "") + money(v) : "—"}</td>
                  <td style={{ padding: "10px 12px" }}>
                    {row.act > 0 ? (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, color: v >= 0 ? t.accent.green : t.accent.red, background: v >= 0 ? `${t.accent.green}18` : `${t.accent.red}18` }}>
                        {v >= 0 ? `✓ ${w.saving}` : `✗ ${w.overrun}`}
                      </span>
                    ) : <span style={{ fontSize: 11, color: t.text.muted }}>{w.notEntered}</span>}
                  </td>
                </tr>
              );
            })}
            {scrapCost > 0 && (
              <tr style={{ borderTop: `1px solid ${t.border.subtle}`, background: `${t.accent.red}08` }}>
                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600, color: t.accent.red }}>🗑️ {w.scrap || "Hurda"}</td>
                <td style={{ padding: "10px 12px", fontSize: 13, color: t.text.muted }}>—</td>
                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: t.accent.red }}>{money(scrapCost)}</td>
                <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: t.accent.red }}>-{money(scrapCost)}</td>
                <td style={{ padding: "10px 12px" }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 10, color: t.accent.red, background: `${t.accent.red}18` }}>✗ {w.loss}</span>
                </td>
              </tr>
            )}
            <tr style={{ borderTop: `2px solid ${t.border.default}`, background: t.bg.theadRow }}>
              <td style={{ padding: "12px", fontSize: 13, fontWeight: 700, color: t.text.primary }}>{w.total || "TOPLAM"}</td>
              <td style={{ padding: "12px", fontSize: 13, fontWeight: 700, color: t.text.primary }}>{money(stdTotal)}</td>
              <td style={{ padding: "12px", fontSize: 13, fontWeight: 700, color: actTotal > stdTotal ? t.accent.red : t.accent.green }}>{actTotal > 0 ? money(actTotal + scrapCost) : "—"}</td>
              <td style={{ padding: "12px", fontSize: 14, fontWeight: 800, color: variance >= 0 ? t.accent.green : t.accent.red }}>{actTotal > 0 ? (variance >= 0 ? "+" : "") + money(variance - scrapCost) : "—"}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>

      {["admin", "manager", "production_manager"].includes(localStorage.getItem("role")) && (
        <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 12, padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 14 }}>
            ✏️ {w.actCostEntry}
          </div>
          {msg && (
            <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13, fontWeight: 600, background: msg.type === "success" ? `${t.accent.green}18` : `${t.accent.red}18`, color: msg.type === "success" ? t.accent.green : t.accent.red, border: `1px solid ${msg.type === "success" ? t.accent.green : t.accent.red}44` }}>
              {msg.text}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginBottom: 14 }}>
            {[
              { key: "actual_material_cost", label: `${w.material} (${getCurrencySymbol()})`, placeholder: `${w.standard}: ${money(stdMat)}` },
              { key: "actual_labor_cost",    label: `${w.labor} (${getCurrencySymbol()})`, placeholder: `${w.standard}: ${money(stdLab)}` },
              { key: "actual_overhead_cost", label: `${w.overhead} (${getCurrencySymbol()})`, placeholder: `${w.standard}: ${money(stdOver)}` },
            ].map(field => (
              <div key={field.key}>
                <div style={{ fontSize: 11, color: t.text.muted, marginBottom: 6, fontWeight: 600 }}>{field.label}</div>
                <input type="number" min="0" step="0.01" value={form[field.key]}
                  onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
                  placeholder={field.placeholder}
                  style={{ width: "100%", padding: "10px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
              </div>
            ))}
          </div>
          {actTotal > 0 && (
            <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 14, background: variance >= 0 ? `${t.accent.green}0f` : `${t.accent.red}0f`, border: `1px solid ${variance >= 0 ? t.accent.green : t.accent.red}33`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: 12, color: t.text.muted }}>{w.totalActual}: <strong style={{ color: actTotal > stdTotal ? t.accent.red : t.accent.green }}>{money(actTotal)}</strong></span>
              <span style={{ fontSize: 13, fontWeight: 700, color: variance >= 0 ? t.accent.green : t.accent.red }}>
                {variance >= 0 ? "✅ +" : "⚠️ "}{money(variance)} ({variance >= 0 ? (w.saving) : (w.overrun)} %{Math.abs(varPct)})
              </span>
            </div>
          )}
          <button onClick={handleSave} disabled={saving} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: saving ? t.bg.toggle : "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: saving ? "default" : "pointer", fontSize: 13, fontWeight: 700 }}>
            {saving ? (w.saving_) : `💾 ${w.save}`}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── İŞ EMRİ DETAY MODAL ─────────────────────────────────────────────────
const WorkOrderDetailModal = ({ order, onClose, onEdit, onRefresh, t, themeName }) => {
  const { t: l } = useLanguage();
  const w = l.workorders || {};
  const { canView } = useContentPermissions("workorders");
  const STATUS_CFG = getStatusCfg();
  const [tab, setTab] = useState("overview");
  const [currentStatus, setCurrentStatus] = useState(order.status);
  const [machines, setMachines] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [assignForm, setAssignForm] = useState({
    machine: order.machine_id || order.machine || "",
    assigned_to: order.assigned_to || "",
  });
  const [loading, setLoading] = useState(false);
  const [prodMsg, setProdMsg] = useState(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [createDepotOrder, setCreateDepotOrder] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelMsg, setCancelMsg] = useState(null);
  const [completedQty, setCompletedQty] = useState(
    Number(order.produced_quantity || 0)
  );

  const detailProducedQty = Number(
    order.produced_quantity ??
    order.produced_qty ??
    order.completed_quantity ??
    0
  );

  const detailCompletionPct =
    Number(order.completion_pct) > 0
      ? Number(order.completion_pct)
      : Number(order.quantity) > 0
        ? Math.min(
            100,
            Math.round(
              (detailProducedQty / Number(order.quantity)) * 1000
            ) / 10
          )
        : 0;
  const [completeLoading, setCompleteLoading] = useState(false);
  
  const [runtimeInfo, setRuntimeInfo] = useState(null);
  const [runtimeLoading, setRuntimeLoading] = useState(false);
  const [runtimeActionLoading, setRuntimeActionLoading] = useState(false);
  const [runtimeError, setRuntimeError] = useState("");

  const [activeDowntime, setActiveDowntime] = useState(null);
  const [downtimeReasons, setDowntimeReasons] = useState([]);
  const [downtimeLoading, setDowntimeLoading] = useState(false);
  const [downtimeSaving, setDowntimeSaving] = useState(false);
  const [downtimeError, setDowntimeError] = useState("");
  const [downtimeNote, setDowntimeNote] = useState("");
  const [showDowntimePanel, setShowDowntimePanel] = useState(false);
const [revisions, setRevisions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);

  const rawUserRole = localStorage.getItem("role") || "";
  const userRole = rawUserRole.trim().toLocaleLowerCase("tr-TR");

  const canManage = [
    "admin",
    "manager",
    "production_manager",
    "maintenance_chief",
    "superadmin",
  ].includes(userRole);

  const canOperateRuntime =
    canManage ||
    ["operator", "operatör"].includes(userRole);
  const sc_ = STATUS_CFG[order.status] || STATUS_CFG.draft;
  const canCancel = canManage && !["completed", "cancelled", "closed"].includes(order.status);

  useEffect(() => {
    fetch(`${API}/machines`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(m => setMachines(Array.isArray(m) ? m : (m.machines || [])))
      .catch(() => {});
    fetch(`${API}/work-orders/${order.id}/material-check`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(mat => setMaterials(Array.isArray(mat) ? mat : []))
      .catch(() => {});
    fetch(`${API}/users`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(usr => {
        const list = Array.isArray(usr) ? usr : [];
        setPersonnel(list.filter(u => ["operator", "operatör", "Operator"].includes(u.role)));
      })
      .catch(() => {});

    fetch(`${API}/work-orders/${order.id}/revisions`, { headers: headers() })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => setRevisions(d.items || []))
      .catch(() => setRevisions([]));

    fetch(`${API}/work-orders/${order.id}/audit-logs`, { headers: headers() })
      .then(r => r.ok ? r.json() : { items: [] })
      .then(d => setAuditLogs(d.items || []))
      .catch(() => setAuditLogs([]));
  }, [order.id, userRole]);


  const fetchRuntimeStatusRef = useRef(null);
  const fetchActiveDowntimeRef = useRef(null);

  const fetchRuntimeStatus = async (silent = false) => {
    if (!order?.id) return;

    if (!silent) {
      setRuntimeLoading(true);
    }

    try {
      const res = await fetch(
        `${API}/work-orders/${order.id}/runtime-status`,
        {
          headers: headers(),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.detail || w.runtimeStatusLoadFailed
        );
      }

      setRuntimeInfo(data);
      setRuntimeError("");
    } catch (e) {
      setRuntimeError(
        e.message || w.runtimeStatusLoadFailed
      );
    } finally {
      if (!silent) {
        setRuntimeLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!order?.id || tab !== "production") return undefined;

    fetchRuntimeStatusRef.current?.();
    fetchActiveDowntimeRef.current?.(true);

    const timer = setInterval(() => {
      fetchRuntimeStatusRef.current?.(true);
      fetchActiveDowntimeRef.current?.(true);
    }, 5000);

    return () => clearInterval(timer);
  }, [order?.id, tab]);

  const resolveRuntimeMachineId = () => {
    return (
      runtimeInfo?.runtime?.machine_id ||
      runtimeInfo?.machine_id ||
      runtimeInfo?.machine_live?.machine_id ||
      order?.machine_id ||
      order?.machine ||
      assignForm?.machine ||
      ""
    );
  };

  const formatRuntimeSeconds = value => {
    const total = Math.max(0, Number(value || 0));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = Math.floor(total % 60);

    return [
      String(hours).padStart(2, "0"),
      String(minutes).padStart(2, "0"),
      String(seconds).padStart(2, "0"),
    ].join(":");
  };

  const fetchDowntimeReasons = async () => {
    if (downtimeReasons.length > 0) return;

    try {
      const res = await fetch(`${API}/downtime-reasons`, {
        headers: headers(),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.detail || w.downtimeReasonsLoadFailed
        );
      }

      setDowntimeReasons(
        Array.isArray(data?.reasons) ? data.reasons : []
      );
    } catch (e) {
      setDowntimeError(
        e.message || w.downtimeReasonsLoadFailed
      );
    }
  };

  const fetchActiveDowntime = async (silent = true) => {
    const machineId = resolveRuntimeMachineId();

    if (!machineId) {
      setActiveDowntime(null);
      return;
    }

    if (!silent) {
      setDowntimeLoading(true);
    }

    try {
      const res = await fetch(
        `${API}/machines/${encodeURIComponent(machineId)}/active-downtime`,
        {
          headers: headers(),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.detail || w.activeDowntimeLoadFailed
        );
      }

      const downtime = data?.downtime || null;
      setActiveDowntime(downtime);
      setDowntimeError("");

      if (
        downtime &&
        !downtime.reason_code &&
        runtimeInfo?.runtime_status === "active"
      ) {
        setShowDowntimePanel(true);
        fetchDowntimeReasons();
      }

      if (!downtime) {
        setShowDowntimePanel(false);
        setDowntimeNote("");
      }
    } catch (e) {
      if (!silent) {
        setDowntimeError(
          e.message || w.activeDowntimeLoadFailed
        );
      }
    } finally {
      if (!silent) {
        setDowntimeLoading(false);
      }
    }
  };

  fetchRuntimeStatusRef.current = fetchRuntimeStatus;
  fetchActiveDowntimeRef.current = fetchActiveDowntime;

  const saveDowntimeReason = async reason => {
    const machineId = resolveRuntimeMachineId();

    if (!machineId || !activeDowntime?.id) return;

    if (Number(reason?.requires_note || 0) === 1 && !downtimeNote.trim()) {
      setDowntimeError(
        w.downtimeNoteRequired
      );
      return;
    }

    setDowntimeSaving(true);
    setDowntimeError("");

    try {
      const res = await fetch(
        `${API}/machines/${encodeURIComponent(machineId)}/active-downtime/reason`,
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify({
            reason_code: reason.code,
            note: downtimeNote.trim(),
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.detail || w.downtimeReasonSaveFailed
        );
      }

      setActiveDowntime(data?.downtime || activeDowntime);
      setShowDowntimePanel(false);
      setDowntimeNote("");

      await fetchActiveDowntime(true);
    } catch (e) {
      setDowntimeError(
        e.message || w.downtimeReasonSaveFailed
      );
    } finally {
      setDowntimeSaving(false);
    }
  };

  const handleRuntimeStart = async () => {
    const machineId = resolveRuntimeMachineId();

    if (!machineId) {
      setRuntimeError(
        w.assignedMachineNotFound
      );
      return;
    }

    const confirmed = window.confirm(
      w.runtimeStartConfirm
    );

    if (!confirmed) return;

    setRuntimeActionLoading(true);
    setRuntimeError("");

    try {
      const res = await fetch(
        `${API}/work-orders/${order.id}/runtime-start`,
        {
          method: "POST",
          headers: headers(),
          body: JSON.stringify({
            machine_id: machineId,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(
          data.detail || w.runtimeStartFailed
        );
      }

      await fetchRuntimeStatus(true);
      await fetchActiveDowntime(true);

      if (onRefresh) {
        onRefresh();
      }
    } catch (e) {
      setRuntimeError(
        e.message || w.runtimeStartFailed
      );
    } finally {
      setRuntimeActionLoading(false);
    }
  };

  const handleStartProduction = async () => {
    setLoading(true); setProdMsg(null);
    try {
      const res = await fetch(`${API}/work-orders/${order.id}/start-production`, {
        method: "PATCH", headers: headers(), body: JSON.stringify(assignForm),
      });
      const data = await res.json();
      if (res.ok) {
            if (data.ok === false || data.has_shortage) {
                setProdMsg({
                    type: "shortage",
                    message: data.message || w.materialShortageProductionBlocked,
                    missing_materials: data.missing_materials || []
                });
                setLoading(false);
                return;
            }

            setCurrentStatus("in_progress");
            await fetchRuntimeStatus(true);
      onRefresh();
        } else {
        setProdMsg({ type: "error", message: data.detail || (w.errGeneric) });
      }
    } catch (e) {
      setProdMsg({ type: "error", message: (w.connectionError) + ": " + e.message });
    }
    setLoading(false);
  };

  // VIDEO-P7-P19G — demo video/manual production completion only
  // Real/field hosts keep the existing runtime safety guards unchanged.
  const isDemoHost = window.location.hostname === "demo.opement.com";

  const handleCompleteProduction = async () => {
    setCompleteLoading(true);
    try {
      const res = await fetch(`${API}/work-orders/${order.id}/complete-production`, {
        method: "PATCH", headers: headers(),
        body: JSON.stringify({ completed_quantity: completedQty }),
      });
      const data = await res.json();
      if (res.ok) {
        setCurrentStatus("qc_pending");
        setTimeout(() => { onRefresh(); }, 500);
      } else {
        setProdMsg({ type: "error", message: data.detail || w.errGeneric });
      }
    } catch (e) {
      setProdMsg({ type: "error", message: w.connectionError + ": " + e.message });
    }
    setCompleteLoading(false);
  };

  const handleCancel = async () => {
    if (!cancelReason.trim()) { setCancelMsg({ type: "error", text: w.cancelReasonRequired }); return; }
    setCancelLoading(true); setCancelMsg(null);
    try {
      const res = await fetch(`${API}/work-orders/${order.id}/cancel`, {
        method: "PATCH", headers: headers(), body: JSON.stringify({ reason: cancelReason, create_depot_order: createDepotOrder }),
      });
      const data = await res.json();
      if (res.ok) {
        setCancelMsg({
          type: "success",
          text: data.stok_eklendi > 0
            ? `${w.cancelledWithStock.replace("{n}", data.stok_eklendi)}`
            : (w.cancelled_)
        });
        setTimeout(() => { onRefresh(); }, 1500);
      } else {
        setCancelMsg({ type: "error", text: data.detail || (w.cancelFailed) });
      }
    } catch (e) {
      setCancelMsg({ type: "error", text: w.connectionError });
    }
    setCancelLoading(false);
  };

  const isAssemblyProduct =
    Number(order.requires_assembly || 0) === 1 ||
    ["mamul", "yari_mamul", "yarı_mamul"].includes(String(order.category || order.product_type || "").toLowerCase());

  const assemblyWorkflowStatuses = [
    "assembly_pending",
    "assembly_in_progress",
    "assembly_done",
    "assembly_completed"
  ];

  const productionActionAllowed =
    !assemblyWorkflowStatuses.includes(currentStatus) &&
    (Boolean(order.parent_order_no) || !isAssemblyProduct);

  const inputStyle = {
    width: "100%", padding: "9px 12px", background: t.bg.input,
    border: `1px solid ${t.border.input}`, borderRadius: 8,
    color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 760, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `${sc_.color}18`, border: `1px solid ${sc_.color}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>📋</div>
            <div>
              <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>{order.order_no || `#${order.id}`}</div>
              <div style={{ fontSize: 13, color: t.text.muted }}>{order.product_name || order.product_code || "—"} · {order.customer || "—"}</div>
            </div>
            <Badge status={order.status} themeName={themeName} parentOrderNo={order.parent_order_no} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            {canManage && (
              <button onClick={() => onEdit(order)} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${t.accent.blueBorder}`, background: t.accent.blueBg, color: t.accent.blue, cursor: "pointer", fontSize: 12 }}>
                ✏️ {w.edit}
              </button>
            )}
            {canCancel && (
              <button onClick={() => setShowCancelConfirm(true)} style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid rgba(248,113,113,0.4)", background: "rgba(248,113,113,0.1)", color: "#f87171", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
                🚫 {w.cancelOrder}
              </button>
            )}
            <button onClick={onClose} style={{ padding: "8px 14px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 12 }}>✕</button>
          </div>
        </div>

        {/* İptal Onay */}
        {showCancelConfirm && (
          <div style={{ padding: "16px 28px", background: "rgba(248,113,113,0.08)", borderBottom: "1px solid rgba(248,113,113,0.3)" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#f87171", marginBottom: 10 }}>
              ⚠️ {w.cancelConfirm}
            </div>
            {currentStatus === "in_progress" && (
              <div style={{ fontSize: 12, color: "#06b6d4", marginBottom: 10 }}>
                ℹ️ {w.cancelInProgressNote}
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <input type="checkbox" id="depoCb" checked={createDepotOrder} onChange={e => setCreateDepotOrder(e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer" }} />
              <label htmlFor="depoCb" style={{ fontSize: 13, color: t.text.secondary, cursor: "pointer" }}>
                {w.createDepotOrder}
              </label>
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)}
                placeholder={w.cancelReasonPlaceholder}
                rows={2}
                style={{ flex: 1, padding: "9px 12px", background: t.bg.input, border: "1px solid rgba(248,113,113,0.4)", borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", resize: "none" }} />
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <button onClick={handleCancel} disabled={cancelLoading} style={{ padding: "9px 18px", borderRadius: 8, border: "none", background: "#ef4444", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 700, opacity: cancelLoading ? 0.6 : 1 }}>
                  {cancelLoading ? "..." : `✓ ${w.yesCancelIt}`}
                </button>
                <button onClick={() => { setShowCancelConfirm(false); setCancelReason(""); setCancelMsg(null); }} style={{ padding: "9px 18px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>
                  {w.goBack}
                </button>
              </div>
            </div>
            {cancelMsg && (
              <div style={{ marginTop: 10, padding: "9px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, background: cancelMsg.type === "success" ? "rgba(16,185,129,0.12)" : "rgba(248,113,113,0.12)", color: cancelMsg.type === "success" ? "#10b981" : "#f87171", border: `1px solid ${cancelMsg.type === "success" ? "rgba(16,185,129,0.3)" : "rgba(248,113,113,0.3)"}` }}>
                {cancelMsg.type === "success" ? "✅ " : "⚠️ "}{cancelMsg.text}
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: "flex", gap: 2, padding: "0 28px", borderBottom: `1px solid ${t.border.divider}`, background: t.bg.header }}>
          {[
            ["overview", w.tabs?.overview, true],
            ["production", w.tabs?.production, canView("tab_production")],
            ["materials", w.tabs?.materials, canView("tab_materials")],
            ["cost", "💰 " + w.cost, canView("tab_cost")],
            ["revisions", w.tabs?.revisions, canView("tab_revisions")],
            ["audit", w.tabs?.audit, canView("tab_audit")],
          ].filter(([, , visible]) => visible).map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{ padding: "11px 14px", background: "none", border: "none", cursor: "pointer", color: tab === id ? t.accent.blue : t.text.muted, fontSize: 13, fontWeight: 600, borderBottom: `2px solid ${tab === id ? t.accent.blue : "transparent"}`, transition: "all 0.2s" }}>{label}</button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 28, background: t.bg.modal }}>

          {tab === "overview" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>📋 {w.orderInfo}</div>
                  {[
                    [w.orderNo,        order.order_no],
                    [w.customerL,           order.customer],
                    [w.productCode,         order.product_code],
                    [w.prodQty,    `${order.quantity} ${order.unit || w.pcs}`],
                    [w.salesQty, order.sales_order_qty ? `${order.sales_order_qty} ${order.unit || w.pcs}` : "—"],
                    [w.machine_,            order.machine_id || order.machine],
                    [w.operator_,          order.assigned_to || "—"],
                    [w.sourceOrder,    order.sales_order_no || "—"],
                    [w.deadline,            order.planned_end || order.deadline],
                    [w.priority_,           getPriorityCfg()[order.priority]?.label || order.priority],
                    [w.notes_,            order.notes],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                      <span style={{ color: t.text.label }}>{k}</span>
                      <span style={{ color: t.text.primary, fontWeight: 500 }}>{v || "—"}</span>
                    </div>
                  ))}
                </div>
                <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 14, textTransform: "uppercase", letterSpacing: "0.06em" }}>📊 {w.productionStatus}</div>
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: t.text.muted }}>{w.completion}</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: t.accent.green }}>{detailCompletionPct}%</span>
                    </div>
                    <div style={{ height: 10, background: t.bg.tag, borderRadius: 5, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${detailCompletionPct}%`, background: `linear-gradient(90deg,${t.accent.green}88,${t.accent.green})`, borderRadius: 5 }} />
                    </div>
                  </div>
                  {[
                    [w.plannedQty, `${order.quantity} ${order.unit || w.pcs}`],
                    [w.producedQty,  `${detailProducedQty} ${order.unit || w.pcs}`],
                    [w.createdBy,        order.created_by],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: `1px solid ${t.border.subtle}`, fontSize: 13 }}>
                      <span style={{ color: t.text.label }}>{k}</span>
                      <span style={{ color: t.text.primary, fontWeight: 500 }}>{v || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === "production" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {prodMsg?.type === "error" && (
                <div style={{ padding: "12px 16px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 10, fontSize: 13, color: t.accent.red }}>
                  ⚠️ {prodMsg.message}
                </div>
              )}
              {prodMsg?.type === "shortage" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ padding: "14px 18px", borderRadius: 12, fontWeight: 700, fontSize: 14, background: t.accent.yellowBg, border: `1px solid ${t.accent.yellow}44`, color: t.accent.yellow }}>
                    ⚠️ {prodMsg.message}
                  </div>
                  <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, overflow: "hidden" }}>
                    <div style={{ padding: "12px 16px", borderBottom: `1px solid ${t.border.default}`, fontSize: 13, fontWeight: 700, color: t.accent.red }}>🛒 {w.purchaseCreated}</div>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: t.bg.theadRow }}>
                          {[w.material, w.needed, w.available_, w.shortage_, w.requestNo].map(h => (
                            <th key={h} style={{ padding: "9px 12px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {prodMsg.missing_materials.map((m, i) => (
                          <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}` }}>
                            <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 600, color: t.text.primary }}>{m.material}</td>
                            <td style={{ padding: "9px 12px", fontSize: 13, color: t.accent.blue }}>{m.needed}</td>
                            <td style={{ padding: "9px 12px", fontSize: 13, color: t.accent.yellow }}>{m.available}</td>
                            <td style={{ padding: "9px 12px", fontSize: 13, fontWeight: 700, color: t.accent.red }}>{m.shortage}</td>
                            <td style={{ padding: "9px 12px", fontSize: 12, color: t.accent.purple }}>{m.request_no}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ padding: "10px 16px", fontSize: 12, color: t.text.muted, borderTop: `1px solid ${t.border.default}` }}>
                      ℹ️ {w.purchaseInfo}
                    </div>
                  </div>
                  <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: "none", background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, alignSelf: "flex-start" }}>
                    {w.okClose}
                  </button>
                </div>
              )}
              {!prodMsg && (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {!canManage && currentStatus !== "in_progress" && (
                    <div style={{ padding: "12px 16px", background: t.accent.yellowBg, border: `1px solid ${t.accent.yellow}44`, borderRadius: 10, fontSize: 13, color: t.accent.yellow }}>
                      ⚠️ {w.noManagerPermission}
                    </div>
                  )}
                  {currentStatus === "in_progress" && (
                    <div style={{ padding: "12px 16px", background: t.accent.greenBg, border: `1px solid ${t.accent.green}44`, borderRadius: 10, fontSize: 13, color: t.accent.green, fontWeight: 600 }}>
                      ✅ {w.alreadyInProduction}
                    </div>
                  )}
                  {canManage && (
                  <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.default}`, borderRadius: 14, padding: 20 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 16 }}>🔧 {w.assignMachineOp}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
                      <div>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 6 }}>{w.machine_}</label>
                        <select value={assignForm.machine} onChange={e => setAssignForm(p => ({ ...p, machine: e.target.value }))} style={inputStyle}>
                          <option value="">{w.select}</option>
                          {machines.map(m => <option key={m.machine} value={m.machine}>{m.machine}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", marginBottom: 6 }}>{w.operator_}</label>
                        <select value={assignForm.assigned_to} onChange={e => setAssignForm(p => ({ ...p, assigned_to: e.target.value }))} style={inputStyle}>
                          <option value="">{w.select}</option>
                          {personnel.map(u => (
                            <option key={u.id} value={u.username}>{u.username}{u.full_name ? ` — ${u.full_name}` : ""}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  )}
                  {canManage && (
                    <div style={{ padding: "12px 16px", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, borderRadius: 10, fontSize: 12, color: t.accent.blue }}>
                      ℹ️ {w.materialInfo || "\"Üretime Al\" butonuna basıldığında sistem malzeme kontrolü yapar."}
                    </div>
                  )}
                  {canManage && productionActionAllowed && currentStatus !== "in_progress" && currentStatus !== "completed" && (
                    <button onClick={handleStartProduction} disabled={loading} style={{ padding: "12px 24px", borderRadius: 10, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#7c3aed,#a78bfa)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 14, fontWeight: 700, alignSelf: "flex-start", boxShadow: loading ? "none" : "0 4px 14px rgba(124,58,237,0.4)" }}>
                      {loading ? (w.processing) : `🏭 ${w.startProduction}`}
                    </button>
                  )}
                  {canOperateRuntime && productionActionAllowed && currentStatus === "in_progress" && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

                      {runtimeError && (
                        <div style={{
                          padding: "12px 16px",
                          background: t.accent.redBg || "rgba(239,68,68,0.12)",
                          border: `1px solid ${t.accent.red}55`,
                          borderRadius: 10,
                          color: t.accent.red,
                          fontSize: 13,
                          fontWeight: 600
                        }}>
                          ⚠️ {runtimeError}
                        </div>
                      )}

                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                        gap: 12
                      }}>
                        <div style={{
                          padding: 16,
                          borderRadius: 12,
                          background: t.bg.metricCard,
                          border: `1px solid ${t.border.default}`
                        }}>
                          <div style={{
                            fontSize: 11,
                            color: t.text.label,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            marginBottom: 8
                          }}>
                            {w.mesStatus}
                          </div>

                          <div style={{
                            fontSize: 15,
                            fontWeight: 800,
                            color:
                              runtimeInfo?.runtime_status === "ready"
                                ? t.accent.blue
                                : runtimeInfo?.runtime_status === "paused"
                                  ? t.accent.yellow
                                  : runtimeInfo?.runtime_status === "active"
                                    ? t.accent.green
                                    : t.text.muted
                          }}>
                            {runtimeLoading
                              ? w.checkingStatus
                              : runtimeInfo?.runtime_status === "ready"
                                ? w.runtimeReady
                                : runtimeInfo?.runtime_status === "paused"
                                  ? w.runtimePaused
                                  : runtimeInfo?.runtime_status === "active"
                                    ? w.runtimeActive
                                    : w.runtimeNotFound}
                          </div>
                        </div>

                        <div style={{
                          padding: 16,
                          borderRadius: 12,
                          background: t.bg.metricCard,
                          border: `1px solid ${t.border.default}`
                        }}>
                          <div style={{
                            fontSize: 11,
                            color: t.text.label,
                            fontWeight: 700,
                            textTransform: "uppercase",
                            marginBottom: 8
                          }}>
                            {w.machineRealStatus}
                          </div>

                          <div style={{
                            fontSize: 15,
                            fontWeight: 800,
                            color:
                              runtimeInfo?.connection_status !== "online"
                                ? t.text.muted
                                : runtimeInfo?.machine_is_running
                                  ? t.accent.green
                                  : runtimeInfo?.machine_is_stopped
                                    ? t.accent.yellow
                                    : t.text.muted
                          }}>
                            {runtimeLoading
                              ? w.checkingStatus
                              : runtimeInfo?.connection_status !== "online"
                                ? w.machineNoConnection
                                : runtimeInfo?.machine_is_running
                                  ? w.machineRunningLive
                                  : runtimeInfo?.machine_is_stopped
                                    ? w.machineStoppedLive
                                    : w.machineStatusUnknown}
                          </div>

                          <div style={{
                            marginTop: 7,
                            fontSize: 11,
                            color: t.text.muted
                          }}>
                            RPM: {Math.round(
                              Number(
                                runtimeInfo?.machine_live?.spindle_rpm || 0
                              )
                            )}
                            {" · "}
                            Feed: {Number(
                              runtimeInfo?.machine_live?.feed_rate || 0
                            )}
                          </div>
                        </div>
                      </div>

                      {runtimeInfo?.runtime_status === "active" &&
                        runtimeInfo?.connection_status === "online" &&
                        !runtimeInfo?.machine_is_running && (
                        <div style={{
                          padding: "14px 16px",
                          background: t.accent.yellowBg,
                          border: `1px solid ${t.accent.yellow}55`,
                          borderRadius: 10,
                          color: t.accent.yellow,
                          fontSize: 13,
                          fontWeight: 700,
                          lineHeight: 1.5
                        }}>
                          {w.runtimeMachineWaitingWarning}
                        </div>
                      )}

                      {activeDowntime && (
                        <div style={{
                          padding: "16px",
                          borderRadius: 12,
                          border: `1px solid ${
                            activeDowntime.reason_code
                              ? t.accent.green + "55"
                              : t.accent.yellow + "66"
                          }`,
                          background: activeDowntime.reason_code
                            ? t.accent.greenBg
                            : t.accent.yellowBg,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 14,
                          flexWrap: "wrap"
                        }}>
                          <div>
                            <div style={{
                              fontSize: 13,
                              fontWeight: 800,
                              color: activeDowntime.reason_code
                                ? t.accent.green
                                : t.accent.yellow
                            }}>
                              {activeDowntime.reason_code
                                ? `✅ ${w.downtimeReasonLabel || "Duruş nedeni"}: ${
                                    activeDowntime.reason_name ||
                                    activeDowntime.reason_code
                                  }`
                                : w.machineDowntimeDetected}
                            </div>

                            <div style={{
                              marginTop: 5,
                              color: t.text.secondary,
                              fontSize: 12,
                              lineHeight: 1.5
                            }}>
                              {w.machineStatusLabel}: {
                                l.machineStatus?.[activeDowntime.machine_status || "stopped"] ||
                                  activeDowntime.machine_status ||
                                  l.machineStatus?.stopped ||
                                  "stopped"
                              }
                              {" · "}
                              {w.durationLabel}: {
                                formatRuntimeSeconds(
                                  activeDowntime.duration_seconds
                                )
                              }
                              {activeDowntime.operator_name
                                ? ` · ${w.downtimeOperator}: ${activeDowntime.operator_name}`
                                : ""}
                            </div>
                          </div>

                          {!activeDowntime.reason_code && (
                            <button
                              onClick={() => {
                                setShowDowntimePanel(true);
                                fetchDowntimeReasons();
                              }}
                              style={{
                                padding: "11px 17px",
                                border: "none",
                                borderRadius: 9,
                                background:
                                  "linear-gradient(135deg,#d97706,#f59e0b)",
                                color: "#fff",
                                cursor: "pointer",
                                fontSize: 13,
                                fontWeight: 800
                              }}
                            >
                              {w.selectDowntimeReason}
                            </button>
                          )}
                        </div>
                      )}

                      {showDowntimePanel && activeDowntime && (
                        <div style={{
                          position: "fixed",
                          inset: 0,
                          zIndex: 100000,
                          background: "rgba(0,0,0,0.72)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          padding: 18
                        }}>
                          <div style={{
                            width: "min(760px, 100%)",
                            maxHeight: "90vh",
                            overflowY: "auto",
                            borderRadius: 18,
                            border: `1px solid ${t.border.default}`,
                            background: t.bg.card,
                            boxShadow: "0 24px 80px rgba(0,0,0,0.45)",
                            padding: 22
                          }}>
                            <div style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              gap: 15,
                              marginBottom: 18
                            }}>
                              <div>
                                <div style={{
                                  fontSize: 20,
                                  fontWeight: 900,
                                  color: t.text.primary
                                }}>
                                  {w.whyMachineStopped}
                                </div>

                                <div style={{
                                  fontSize: 13,
                                  color: t.text.secondary,
                                  marginTop: 6,
                                  lineHeight: 1.5
                                }}>
                                  {w.machineStatusLabel}{" "}
                                  <strong>
                                    {l.machineStatus?.[activeDowntime.machine_status || "stopped"] ||
                                  activeDowntime.machine_status ||
                                  l.machineStatus?.stopped ||
                                  "stopped"}
                                  </strong>
                                  {" · "}
                                  {w.downtimeDurationLabel || "Duruş süresi"}:{" "}
                                  <strong>
                                    {formatRuntimeSeconds(
                                      activeDowntime.duration_seconds
                                    )}
                                  </strong>
                                </div>
                              </div>

                              <button
                                onClick={() => setShowDowntimePanel(false)}
                                disabled={downtimeSaving}
                                style={{
                                  width: 38,
                                  height: 38,
                                  borderRadius: 10,
                                  border: `1px solid ${t.border.default}`,
                                  background: t.bg.metricCard,
                                  color: t.text.primary,
                                  cursor: downtimeSaving
                                    ? "default"
                                    : "pointer",
                                  fontSize: 19
                                }}
                              >
                                ×
                              </button>
                            </div>

                            {downtimeError && (
                              <div style={{
                                padding: "11px 13px",
                                borderRadius: 9,
                                marginBottom: 14,
                                background:
                                  t.accent.redBg ||
                                  "rgba(239,68,68,0.12)",
                                color: t.accent.red,
                                fontSize: 13,
                                fontWeight: 700
                              }}>
                                ⚠️ {downtimeError}
                              </div>
                            )}

                            <div style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fit,minmax(180px,1fr))",
                              gap: 11
                            }}>
                              {downtimeReasons.map(reason => (
                                <button
                                  key={reason.id || reason.code}
                                  onClick={() =>
                                    saveDowntimeReason(reason)
                                  }
                                  disabled={downtimeSaving}
                                  style={{
                                    minHeight: 76,
                                    padding: "14px",
                                    borderRadius: 12,
                                    border:
                                      `1px solid ${t.border.default}`,
                                    background: t.bg.metricCard,
                                    color: t.text.primary,
                                    cursor: downtimeSaving
                                      ? "default"
                                      : "pointer",
                                    textAlign: "left"
                                  }}
                                >
                                  <div style={{
                                    fontSize: 14,
                                    fontWeight: 800
                                  }}>
                                    {reason.icon
                                      ? `${reason.icon} `
                                      : ""}
                                    {reason.name}
                                  </div>

                                  <div style={{
                                    marginTop: 5,
                                    fontSize: 11,
                                    color: t.text.muted
                                  }}>
                                    {reason.is_planned
                                      ? w.downtimePlanned
                                      : w.downtimeUnplanned}
                                    {reason.requires_note
                                      ? w.downtimeDescriptionRequired
                                      : ""}
                                  </div>
                                </button>
                              ))}
                            </div>

                            {downtimeReasons.length === 0 && (
                              <div style={{
                                padding: 18,
                                color: t.text.secondary,
                                textAlign: "center"
                              }}>
                                {downtimeLoading
                                  ? w.downtimeReasonsLoading
                                  : w.noDowntimeReason}
                              </div>
                            )}

                            <div style={{ marginTop: 17 }}>
                              <label style={{
                                display: "block",
                                fontSize: 12,
                                fontWeight: 700,
                                color: t.text.secondary,
                                marginBottom: 7
                              }}>
                                {w.downtimeDescriptionLabel}
                              </label>

                              <textarea
                                value={downtimeNote}
                                onChange={e =>
                                  setDowntimeNote(e.target.value)
                                }
                                placeholder={w.downtimeDescriptionPlaceholder}
                                rows={3}
                                style={{
                                  width: "100%",
                                  boxSizing: "border-box",
                                  resize: "vertical",
                                  padding: "11px 12px",
                                  borderRadius: 10,
                                  border: `1px solid ${t.border.input}`,
                                  background: t.bg.input,
                                  color: t.text.primary,
                                  fontFamily: "inherit",
                                  fontSize: 13
                                }}
                              />
                            </div>

                            <div style={{
                              marginTop: 13,
                              fontSize: 11,
                              color: t.text.muted,
                              lineHeight: 1.5
                            }}>
                              {w.downtimeReasonInfo}
                            </div>
                          </div>
                        </div>
                      )}

                      <div style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 10
                      }}>
                        {runtimeInfo?.runtime_status === "ready" && (
                          <button
                            onClick={handleRuntimeStart}
                            disabled={runtimeActionLoading}
                            style={{
                              padding: "12px 22px",
                              borderRadius: 10,
                              border: "none",
                              background: runtimeActionLoading
                                ? t.bg.tag
                                : "linear-gradient(135deg,#0284c7,#06b6d4)",
                              color: "#fff",
                              cursor: runtimeActionLoading
                                ? "default"
                                : "pointer",
                              fontSize: 14,
                              fontWeight: 800,
                              boxShadow: runtimeActionLoading
                                ? "none"
                                : "0 4px 16px rgba(6,182,212,0.30)"
                            }}
                          >
                            {runtimeActionLoading
                              ? w.startingProduction
                              : w.startRuntimeProduction}
                          </button>
                        )}

                        <button
                          onClick={() => fetchRuntimeStatus()}
                          disabled={runtimeLoading}
                          style={{
                            padding: "12px 18px",
                            borderRadius: 10,
                            border: `1px solid ${t.border.default}`,
                            background: t.bg.metricCard,
                            color: t.text.primary,
                            cursor: runtimeLoading
                              ? "default"
                              : "pointer",
                            fontSize: 13,
                            fontWeight: 700
                          }}
                        >
                          {runtimeLoading
                            ? w.checkingStatus
                            : w.checkStatus}
                        </button>
                      </div>

                      <div style={{
                        borderTop: `1px solid ${t.border.default}`,
                        paddingTop: 14,
                        display: "flex",
                        flexDirection: "column",
                        gap: 12
                      }}>
                        <div style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          flexWrap: "wrap"
                        }}>
                          <label style={{
                            fontSize: 13,
                            color: t.text.secondary,
                            fontWeight: 600
                          }}>
                            {w.producedQuantity}:
                          </label>

                          <input
                            type="number"
                            value={completedQty}
                            onChange={e =>
                              setCompletedQty(
                                parseInt(e.target.value) || 0
                              )
                            }
                            min="1"
                            max={order.quantity}
                            style={{
                              width: 80,
                              padding: "8px 12px",
                              borderRadius: 8,
                              border: `1px solid ${t.border.input}`,
                              background: t.bg.input,
                              color: t.text.primary,
                              fontSize: 14,
                              fontWeight: 700
                            }}
                          />

                          <span style={{
                            fontSize: 13,
                            color: t.text.muted
                          }}>
                            / {order.quantity} {w.pcs}
                          </span>
                        </div>

                        <button
                            onClick={handleCompleteProduction}
                            disabled={
                              completeLoading ||
                              (
                                !isDemoHost &&
                                (
                                  runtimeInfo?.runtime_status !== "active" ||
                                  runtimeInfo?.connection_status !== "online" ||
                                  runtimeInfo?.machine_is_running
                                )
                              )
                            }
                            title={
                              isDemoHost
                                ? ""
                                : runtimeInfo?.runtime_status !== "active"
                                  ? w.startProductionFirst
                                  : runtimeInfo?.connection_status !== "online"
                                    ? w.cannotCompleteMachineOffline
                                    : runtimeInfo?.machine_is_running
                                      ? w.cannotCompleteMachineRunning
                                      : ""
                            }
                            style={{
                              padding: "12px 24px",
                              borderRadius: 10,
                              border: "none",
                              background:
                                completeLoading ||
                                (
                                  !isDemoHost &&
                                  (
                                    runtimeInfo?.runtime_status !== "active" ||
                                    runtimeInfo?.connection_status !== "online" ||
                                    runtimeInfo?.machine_is_running
                                  )
                                )
                                  ? t.bg.tag
                                  : "linear-gradient(135deg,#059669,#10b981)",
                              color: "#fff",
                              cursor:
                                completeLoading ||
                                (
                                  !isDemoHost &&
                                  (
                                    runtimeInfo?.runtime_status !== "active" ||
                                    runtimeInfo?.connection_status !== "online" ||
                                    runtimeInfo?.machine_is_running
                                  )
                                )
                                  ? "not-allowed"
                                  : "pointer",
                              fontSize: 14,
                              fontWeight: 700,
                              alignSelf: "flex-start",
                              boxShadow:
                                completeLoading ||
                                (
                                  !isDemoHost &&
                                  (
                                    runtimeInfo?.runtime_status !== "active" ||
                                    runtimeInfo?.connection_status !== "online" ||
                                    runtimeInfo?.machine_is_running
                                  )
                                )
                                  ? "none"
                                  : "0 4px 14px rgba(5,150,105,0.4)"
                            }}
                          >
                            {completeLoading
                              ? w.processing
                              : isDemoHost
                                ? w.completeRuntimeProduction
                                : runtimeInfo?.runtime_status !== "active"
                                  ? w.startProductionFirstButton
                                  : runtimeInfo?.connection_status !== "online"
                                    ? w.machineConnectionMissingButton
                                    : runtimeInfo?.machine_is_running
                                      ? w.stopMachineFirstButton
                                      : w.completeRuntimeProduction}
                          </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {tab === "materials" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {materials.length === 0 ? (
                <div style={{ textAlign: "center", color: t.text.placeholder, padding: "40px 0" }}>
                  {w.noBom}
                </div>
              ) : (
                <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: t.bg.theadRow }}>
                        {[w.material, w.needed, w.available_, w.statusLabel].map(h => (
                          <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {materials.map((m, i) => {
                        const ok = m.available >= m.required;
                        return (
                          <tr key={i} style={{ borderTop: `1px solid ${t.border.subtle}` }}>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: t.text.primary, fontWeight: 600 }}>{m.name}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13, color: t.accent.blue }}>{m.required} {m.unit}</td>
                            <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 700, color: ok ? t.accent.green : t.accent.red }}>{m.available} {m.unit}</td>
                            <td style={{ padding: "10px 14px" }}>
                              {ok
                                ? <span style={{ color: t.accent.green, fontSize: 12, fontWeight: 600 }}>✓ {w.sufficient}</span>
                                : <span style={{ color: t.accent.red, fontSize: 12, fontWeight: 600 }}>✗ {w.insufficient} {m.required - m.available} {m.unit}</span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "cost" && (
            <CostTab order={order} t={t} onRefresh={onRefresh} />
          )}

          {tab === "revisions" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {revisions.length === 0 ? (
                <div style={{ color: t.text.placeholder }}>{w.revisionEmpty}</div>
              ) : (
                revisions.map(r => (
                  <div key={r.id} style={{ padding: 16, borderRadius: 12, background: t.bg.metricCard, border: `1px solid ${t.border.default}` }}>
                    <div style={{ fontWeight: 800, color: t.text.primary }}>REV-{r.revision_no}</div>
                    <div style={{ marginTop: 8, color: t.text.secondary }}>{w.revisionQuantity}: {r.old_quantity || "—"} → {r.new_quantity || "—"}</div>
                    <div style={{ color: t.text.secondary }}>{w.revisionDeadline}: {r.old_deadline || "—"} → {r.new_deadline || "—"}</div>
                    <div style={{ color: t.text.secondary }}>
                      {w.revisionPriority}: {
                        getPriorityCfg()[r.old_priority]?.label ||
                        r.old_priority ||
                        "—"
                      } → {
                        getPriorityCfg()[r.new_priority]?.label ||
                        r.new_priority ||
                        "—"
                      }
                    </div>
                    <div style={{ marginTop: 8, color: t.text.primary }}>
                      {w.revisionReason}: {r.reason || "—"}
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: t.text.muted }}>
                      {r.created_at
                        ? new Date(r.created_at).toLocaleString(getLocale())
                        : ""}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {tab === "audit" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {auditLogs.length === 0 ? (
                <div style={{ color: t.text.placeholder }}>{w.auditEmpty}</div>
              ) : (
                auditLogs.map(log => (
                  <div key={log.id} style={{ padding: 16, borderRadius: 12, background: t.bg.metricCard, border: `1px solid ${t.border.default}` }}>
                    <div style={{ fontWeight: 800, color: t.text.primary }}>
                      {log.action === "work_order_updated"
                        ? w.auditActionWorkOrderUpdated
                        : log.action || "—"}
                    </div>

                    <div style={{ marginTop: 8, color: t.text.secondary }}>
                      {log.action === "work_order_updated"
                        ? w.auditDescriptionWorkOrderUpdated
                        : log.description || "—"}
                    </div>

                    <div style={{ marginTop: 6, fontSize: 12, color: t.text.muted }}>
                      {log.created_at
                        ? new Date(log.created_at).toLocaleString(getLocale())
                        : ""}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── İŞ EMRİ FORM MODAL ───────────────────────────────────────────────────
const WorkOrderFormModal = ({ order, onClose, onSaved, users }) => {
  const { t } = useTheme();
  const { t: l } = useLanguage();
  const w = l.workorders || {};
  const isEdit = !!order;
  const [form, setForm] = useState({
    order_no: order?.order_no || "",
    production_type: order?.production_type || "customer",
    customer_name: order?.customer || "",
    customer_type: "domestic",
    product_name: order?.product_name || "",
    product_code: order?.product_code || "",
    quantity: order?.quantity || "",
    unit: order?.unit || "adet",
    machine_id: order?.machine_id || order?.machine || "",
    assigned_to: order?.assigned_to || "",
    planned_start: new Date().toISOString().slice(0, 10),
    deadline: order?.planned_end?.slice(0, 10) || order?.deadline?.slice(0, 10) || "",
    priority: order?.priority || "normal",
    notes: order?.notes || "",
  });
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [customerSearch, setCustomerSearch] = useState(order?.customer || "");
  const [productSearch, setProductSearch] = useState(order?.product_name || "");
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [showProductList, setShowProductList] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mrpData, setMrpData] = useState(null);
  const [mrpLoading, setMrpLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/customers`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(c => setCustomers(Array.isArray(c) ? c : []))
      .catch(() => setCustomers([]));
    fetch(`${API}/product-cards`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(p => setProducts(Array.isArray(p) ? p : []))
      .catch(() => setProducts([]));
  }, []);

  const filteredCustomers = customers.filter(c =>
    c.customer_name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.customer_code?.toLowerCase().includes(customerSearch.toLowerCase())
  );
  const filteredProducts = products.filter(p => {
    const search = productSearch.toLowerCase().replace(/[-\s]/g, "");
    const name = (p.product_name || "").toLowerCase().replace(/[-\s]/g, "");
    const code = (p.product_code || "").toLowerCase().replace(/[-\s]/g, "");
    return name.includes(search) || code.includes(search);
  });

  const selectCustomer = (c) => {
    setCustomerSearch(c.customer_name);
    const prefix = c.customer_type === "foreign" ? "YD" : "YI";
    const rand = Math.floor(Math.random() * 9000) + 1000;
    setForm(p => ({ ...p, customer_name: c.customer_name, customer_type: c.customer_type, order_no: `${prefix}-${new Date().getFullYear()}-${rand}` }));
    setShowCustomerList(false);
  };

  const selectProduct = async (p) => {
    setProductSearch(p.product_name);
    setForm(prev => ({ ...prev, product_name: p.product_name, product_code: p.product_code, unit: p.unit || prev.unit }));
    setShowProductList(false);
    setMrpLoading(true); setMrpData(null);
    try {
      const res = await fetch(`${API}/mrp/calculate/${p.product_code}?quantity=1`, { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setMrpData(data);
        if (data.operation_details?.[0]?.machine_id) {
          setForm(prev => ({ ...prev, machine_id: data.operation_details[0].machine_id }));
        }
      }
    } catch (e) {}
    setMrpLoading(false);
  };

  const calcDeadline = (start, days) => {
    if (!start || !days) return "";
    const d = new Date(start);
    let added = 0;
    while (added < parseInt(days)) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) added++; }
    return d.toISOString().slice(0, 10);
  };

  const handleSave = async () => {
    if (!form.product_name || !form.quantity) { setError(w.errProductQtyRequired); return; }
    setLoading(true); setError("");
    try {
      const url = isEdit ? `${API}/work-orders/${order.id}` : `${API}/work-orders`;
      const body = {
        order_no: form.order_no,
        customer: form.production_type === "stock" ? "DEPO STOĞU" : form.customer_name,
        production_type: form.production_type,
        product_code: form.product_code || form.product_name,
        product_name: form.product_name,
        quantity: parseFloat(form.quantity) || 0,
        machine: form.machine_id,
        assigned_to: form.assigned_to || null,
        deadline: form.deadline || null,
        planned_end: form.deadline || null,
        priority: form.priority,
        notes: form.notes,
        status: isEdit ? order.status : "draft",
      };
      const res = await fetch(url, { method: isEdit ? "PUT" : "POST", headers: headers(), body: JSON.stringify(body) });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(typeof err.detail === "string" ? err.detail : JSON.stringify(err.detail));
      }
      onSaved();
    } catch (e) { setError(e.message); }
    setLoading(false);
  };

  const operators = (users || []).filter(u => u.role === "operator" || u.role === "operatör" || u.role === "Operator");
  const inputStyle = { width: "100%", padding: "10px 12px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 8, color: t.text.primary, fontSize: 13, outline: "none", boxSizing: "border-box" };
  const labelStyle = { display: "block", fontSize: 11, fontWeight: 700, color: t.text.label, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 };

  const mrpMoney = value =>
    `${Number(value || 0).toLocaleString(getLocale(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} ${getCurrencySymbol()}`;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 1100, background: t.bg.overlay, backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} onClick={onClose}>
      <div style={{ background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 20, width: "100%", maxWidth: 700, maxHeight: "90vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: t.shadow.modal }} onClick={e => e.stopPropagation()}>
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${t.border.default}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: t.bg.header }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: t.text.primary }}>
            {isEdit ? `✏️ ${w.editOrder}` : `➕ ${w.new}`}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: t.text.muted, cursor: "pointer", fontSize: 20 }}>✕</button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Üretim Tipi */}
            <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.subtle}`, borderRadius: 12, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 12, textTransform: "uppercase" }}>
                🏭 {w.productionType}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                {[
                  { value: "customer", label: `📋 ${w.prodForCustomer}`, desc: w.prodForCustomerDesc },
                  { value: "stock",    label: `📦 ${w.prodForStock}`,    desc: w.prodForStockDesc },
                ].map(opt => (
                  <div key={opt.value} onClick={() => {
                    setForm(p => ({
                      ...p,
                      production_type: opt.value,
                      customer_name: opt.value === "stock" ? "" : p.customer_name,
                      order_no: opt.value === "stock" ? `STK-${new Date().getFullYear()}-${Math.floor(Math.random() * 9000) + 1000}` : p.order_no,
                    }));
                  }} style={{ flex: 1, padding: "12px 16px", borderRadius: 10, cursor: "pointer", border: `2px solid ${form.production_type === opt.value ? t.accent.blue : t.border.subtle}`, background: form.production_type === opt.value ? t.accent.blueBg : t.bg.input, transition: "all 0.2s" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: form.production_type === opt.value ? t.accent.blue : t.text.primary }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: t.text.muted, marginTop: 3 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Müşteri */}
            {form.production_type === "customer" && (
              <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.subtle}`, borderRadius: 12, padding: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 12, textTransform: "uppercase" }}>👤 {w.customerL}</div>
                <div style={{ position: "relative" }}>
                  <label style={labelStyle}>{w.customerName}</label>
                  <input type="text" value={customerSearch} onChange={e => { setCustomerSearch(e.target.value); setShowCustomerList(true); }} onFocus={() => setShowCustomerList(true)} onBlur={() => setTimeout(() => setShowCustomerList(false), 150)} placeholder={w.searchCustomer} style={inputStyle} />
                  {showCustomerList && filteredCustomers.length > 0 && (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 8, maxHeight: 180, overflowY: "auto", boxShadow: t.shadow.modal, marginTop: 2 }}>
                      {filteredCustomers.map(c => (
                        <div key={c.id} onClick={() => selectCustomer(c)} style={{ padding: "10px 14px", cursor: "pointer", fontSize: 13, borderBottom: `1px solid ${t.border.subtle}`, display: "flex", justifyContent: "space-between" }} onMouseEnter={e => e.currentTarget.style.background = t.bg.rowAlt} onMouseLeave={e => e.currentTarget.style.background = "none"}>
                          <span style={{ color: t.text.primary, fontWeight: 600 }}>{c.customer_name}</span>
                          <span style={{ fontSize: 11, color: t.text.muted }}>{c.customer_code}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {form.order_no && <div style={{ marginTop: 8, padding: "6px 12px", background: t.accent.blueBg, borderRadius: 8, fontSize: 12, color: t.accent.blue }}>📋 {w.orderNo}: <strong>{form.order_no}</strong></div>}
              </div>
            )}

            {/* Stoğa üretim */}
            {form.production_type === "stock" && (
              <div style={{ padding: "12px 16px", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, borderRadius: 10, fontSize: 13, color: t.accent.blue }}>
                📦 {w.stockOrderNo}: <strong>{form.order_no}</strong> — {w.stockOrderNote}
              </div>
            )}

            {/* Ürün */}
            <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.subtle}`, borderRadius: 12, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 12, textTransform: "uppercase" }}>📦 {w.product_}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div style={{ position: "relative", gridColumn: "1/-1" }}>
                  <label style={labelStyle}>{w.productName} *</label>
                  <input type="text" value={productSearch} onChange={e => { setProductSearch(e.target.value); setForm(p => ({ ...p, product_name: e.target.value })); setShowProductList(true); }} onFocus={() => setShowProductList(true)} onBlur={() => setTimeout(() => setShowProductList(false), 150)} placeholder={w.searchProduct} style={inputStyle} />
                  {showProductList && filteredProducts.length > 0 && (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: t.bg.modal, border: `1px solid ${t.border.default}`, borderRadius: 8, maxHeight: 160, overflowY: "auto", boxShadow: t.shadow.modal, marginTop: 2 }}>
                      {filteredProducts.map(p => (
                        <div key={p.id || p.product_code} onClick={() => selectProduct(p)} style={{ padding: "9px 14px", cursor: "pointer", fontSize: 13, borderBottom: `1px solid ${t.border.subtle}`, display: "flex", justifyContent: "space-between" }} onMouseEnter={e => e.currentTarget.style.background = t.bg.rowAlt} onMouseLeave={e => e.currentTarget.style.background = "none"}>
                          <span style={{ color: t.text.primary, fontWeight: 600 }}>{p.product_name}</span>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <span style={{ fontSize: 11, color: t.text.muted }}>{p.product_code}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, color: (p.available_qty || 0) > 0 ? "#00ff9d" : "#06b6d4" }}>{w.stock_}: {p.available_qty || 0}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {mrpLoading && (
                  <div style={{ gridColumn: "1/-1", padding: "10px 14px", background: t.bg.card, borderRadius: 10, fontSize: 13, color: t.text.muted }}>
                    ⏳ {w.mrpCalc}
                  </div>
                )}
                {mrpData && !mrpLoading && (
                  <div style={{ gridColumn: "1/-1", background: t.bg.card, border: `1px solid ${t.border.default}`, borderRadius: 12, padding: 16 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 12, textTransform: "uppercase" }}>📊 {w.mrpCostSummary}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8, marginBottom: 12 }}>
                      {[
                        { label: w.rawMaterial,    value: mrpData.cost_breakdown?.material_cost, color: "#10b981" },
                        { label: w.outsource,       value: mrpData.cost_breakdown?.outsource_cost, color: "#a78bfa" },
                        { label: w.laborSgk, value: mrpData.cost_breakdown?.labor_cost,    color: "#60a5fa" },
                        { label: w.unitCost,value: mrpData.cost_breakdown?.unit_cost,    color: "#f59e0b" },
                      ].map(({ label, value, color }) => (
                        <div key={label} style={{ textAlign: "center", padding: "10px 8px", background: t.bg.metricCard, borderRadius: 10, border: `1px solid ${t.border.subtle}` }}>
                          <div style={{ fontSize: 9, color: t.text.muted, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
                          <div style={{ fontSize: 15, fontWeight: 800, color }}>{mrpMoney(value)}</div>
                        </div>
                      ))}
                    </div>
                    {mrpData.has_shortage && (
                      <div style={{ padding: "8px 12px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, fontSize: 12, color: t.accent.red, fontWeight: 600, marginBottom: 10 }}>
                        ⚠️ {w.mrpShortage}
                      </div>
                    )}
                    {mrpData.materials?.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: t.text.label, marginBottom: 6, textTransform: "uppercase" }}>🔩 {w.rawMaterialStatus}</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {mrpData.materials.map((m, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 10px", background: t.bg.metricCard, borderRadius: 8, border: `1px solid ${t.border.subtle}` }}>
                              <span style={{ fontSize: 12, color: t.text.primary, fontWeight: 600 }}>{m.material_name}</span>
                              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                                <span style={{ fontSize: 11, color: t.text.muted }}>{w.needed}: {m.total_required} {m.unit}</span>
                                <span style={{ fontSize: 11, color: m.available >= m.required_per_unit ? "#10b981" : t.accent.red, fontWeight: 700 }}>{w.inWarehouse}: {m.available}</span>
                                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: m.status === "ok" ? "rgba(16,185,129,0.12)" : "rgba(248,113,113,0.12)", color: m.status === "ok" ? "#10b981" : t.accent.red }}>
                                  {m.status === "ok" ? `✓ ${w.sufficient}` : `✗ ${w.insufficient}`}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {mrpData.outsource_operations?.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: t.text.label, marginBottom: 6, textTransform: "uppercase" }}>🏭 {w.outsourceOps}</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {mrpData.outsource_operations.map((o, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: t.bg.metricCard, borderRadius: 8, border: `1px solid ${t.border.subtle}` }}>
                              <span style={{ fontSize: 12, color: t.text.primary, fontWeight: 600 }}>{o.operation_name}</span>
                              <div style={{ display: "flex", gap: 12 }}>
                                <span style={{ fontSize: 11, color: t.text.muted }}>{o.supplier_name || "—"}</span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: "#a78bfa" }}>{mrpMoney(o.unit_cost)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 10, borderTop: `1px solid ${t.border.subtle}` }}>
                      <span style={{ fontSize: 12, color: t.text.muted }}>{w.totalCostFor} ({form.quantity || 1} {w.pcs}):</span>
                      <span style={{ fontSize: 16, fontWeight: 800, color: "#f59e0b" }}>{mrpMoney(
                        (mrpData.cost_breakdown?.unit_cost || 0) *
                        (parseFloat(form.quantity) || 1)
                      )}</span>
                    </div>
                  </div>
                )}

                <div>
                  <label style={labelStyle}>{w.productCode}</label>
                  <input type="text" value={form.product_code} onChange={e => { setForm(p => ({ ...p, product_code: e.target.value })); setProductSearch(e.target.value); setShowProductList(true); }} style={inputStyle} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <div style={{ flex: 2 }}>
                    <label style={labelStyle}>{w.qty} *</label>
                    <input type="number" value={form.quantity} onChange={e => { setForm(p => ({ ...p, quantity: e.target.value })); }}
                      onBlur={async e => {
                        const qty = e.target.value;
                        if (form.product_code && qty > 0) {
                          setMrpLoading(true);
                          try {
                            const res = await fetch(`${API}/mrp/calculate/${form.product_code}?quantity=${qty}`, { headers: headers() });
                            if (res.ok) setMrpData(await res.json());
                          } catch {}
                          setMrpLoading(false);
                        }
                      }} style={inputStyle} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>{w.unit_}</label>
                    <select value={form.unit} onChange={e => setForm(p => ({ ...p, unit: e.target.value }))} style={inputStyle}>
                      <option value="adet">{w.pcs}</option>
                      <option value="kg">KG</option>
                      <option value="mt">MT</option>
                      <option value="lt">LT</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Üretim Planı */}
            <div style={{ background: t.bg.metricCard, border: `1px solid ${t.border.subtle}`, borderRadius: 12, padding: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: t.text.label, marginBottom: 12, textTransform: "uppercase" }}>📅 {w.productionPlan}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div><label style={labelStyle}>{w.start}</label><input type="date" value={form.planned_start} onChange={e => setForm(p => ({ ...p, planned_start: e.target.value }))} style={inputStyle} /></div>
                <div><label style={labelStyle}>{w.deadlineWorkdays}</label><input type="number" placeholder={w.eg10} onChange={e => setForm(p => ({ ...p, deadline: calcDeadline(form.planned_start, e.target.value) }))} style={inputStyle} min="1" /></div>
                <div><label style={labelStyle}>{w.deadlineDate}</label><input type="date" value={form.deadline} onChange={e => setForm(p => ({ ...p, deadline: e.target.value }))} style={inputStyle} /></div>
                <div><label style={labelStyle}>{w.machine_}</label><input type="text" value={form.machine_id} onChange={e => setForm(p => ({ ...p, machine_id: e.target.value }))} placeholder={w.machineIdPlaceholder} style={inputStyle} /></div>
                <div>
                  <label style={labelStyle}>{w.operatorKiosk}</label>
                  <select value={form.assigned_to} onChange={e => setForm(p => ({ ...p, assigned_to: e.target.value }))} style={inputStyle}>
                    <option value="">-- {w.selectOperator} --</option>
                    {operators.map(u => <option key={u.id} value={u.username}>{u.username}{u.full_name ? ` — ${u.full_name}` : ""}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>{w.priority_}</label>
                  <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} style={inputStyle}>
                    <option value="low">{w.priority?.low}</option>
                    <option value="normal">{w.priority?.normal}</option>
                    <option value="high">{w.priority?.high}</option>
                    <option value="critical">{w.priority?.critical}</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label style={labelStyle}>{w.notes_}</label>
              <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
            </div>

            {error && <div style={{ padding: "10px 16px", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 8, color: t.accent.red, fontSize: 13 }}>{error}</div>}
          </div>
        </div>

        <div style={{ padding: "16px 28px", borderTop: `1px solid ${t.border.default}`, display: "flex", justifyContent: "flex-end", gap: 12, background: t.bg.header }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 8, border: `1px solid ${t.border.input}`, background: "none", color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>
            {w.cancel_}
          </button>
          <button onClick={handleSave} disabled={loading} style={{ padding: "10px 24px", borderRadius: 8, border: "none", background: loading ? t.bg.tag : "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: loading ? "default" : "pointer", fontSize: 13, fontWeight: 600 }}>
            {loading ? (w.saving_) : isEdit ? (w.update_) : (w.create_)}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── İŞ EMRİ KARTI ────────────────────────────────────────────────────────
const WorkOrderCard = ({ order, onClick, t, themeName }) => {
  const { t: l } = useLanguage();
  const w = l.workorders || {};
  const sc_ = sc(order.status, themeName);
  const pct = order.completion_pct ?? 0;
  return (
    <div onClick={() => onClick(order)} style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: 18, cursor: "pointer", transition: "all 0.2s", boxShadow: t.shadow.card, position: "relative", overflow: "hidden" }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.borderColor = `${sc_.color}55`; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.borderColor = t.border.card; }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${sc_.color}, transparent)`, borderRadius: "14px 14px 0 0" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: t.text.primary, marginBottom: 3 }}>{order.order_no || `#${order.id}`}</div>
          <div style={{ fontSize: 12, color: t.text.muted }}>{order.product_name || order.product_code || "—"}</div>
        </div>
        <Badge status={order.status} themeName={themeName} parentOrderNo={order.parent_order_no} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
          <span style={{ fontSize: 11, color: t.text.muted, textTransform: "uppercase", fontWeight: 600 }}>{w.progress}</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: t.accent.green }}>{pct}%</span>
        </div>
        <div style={{ height: 5, background: t.bg.tag, borderRadius: 3, overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${pct}%`, borderRadius: 3, background: `linear-gradient(90deg, ${t.accent.green}88, ${t.accent.green})` }} />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
        {[
          [w.customerL,  order.customer || "—"],
          [w.qty,   `${order.quantity} ${order.unit || w.pcs}`],
          [w.machine_,   order.machine_id || order.machine || "—"],
          [w.operator_, order.assigned_to || "—"],
        ].map(([label, value]) => (
          <div key={label} style={{ background: t.bg.metricCard, borderRadius: 7, padding: "7px 10px", border: `1px solid ${t.border.subtle}` }}>
            <div style={{ fontSize: 9, color: t.text.muted, textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: t.text.primary }}>{value}</div>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <PriorityBadge priority={order.priority} />
        {order.assigned_to && <span style={{ fontSize: 11, color: t.text.muted }}>👤 {order.assigned_to}</span>}
      </div>
    </div>
  );
};

// ─── ANA SAYFA ────────────────────────────────────────────────────────────
export default function WorkOrders() {
  const { t, themeName } = useTheme();
  const { t: l } = useLanguage();
  const w = useMemo(() => l.workorders || {}, [l.workorders]);

  const { canView } = useContentPermissions("workorders");

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [view, setView] = useState(localStorage.getItem("view_workorders") || "list");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [stats, setStats] = useState({});
  const [users, setUsers] = useState([]);
  const [treeModal, setTreeModal] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    const handler = (e) => {
      setEditing({ product_name: e.detail.product_name || "", machine_id: e.detail.machine_id || "", priority: e.detail.priority || "normal", notes: e.detail.notes || "" });
      setShowForm(true);
    };
    window.addEventListener("openWorkOrder", handler);
    return () => window.removeEventListener("openWorkOrder", handler);
  }, []);

  useEffect(() => {
    fetch(`${API}/users`, { headers: headers() })
      .then(r => r.ok ? r.json() : [])
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(() => setUsers([]));
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/work-orders`, { headers: headers() });
      if (!res.ok) throw new Error(w.errFetch);
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.orders || data.items || [];

      const isChild = (o) => !!o.parent_order_no || !!o.parent_order_id || (Number(o.level_no || 0) > 0);

      const rootKey = (o) => {
        if (o.root_order_id) return Number(o.root_order_id);
        if (!isChild(o) && o.id) return Number(o.id);
        const no = o.order_no || "";
        const m = no.match(/^(IE-\d{4}-\d{4})/);
        return m ? m[1] : no;
      };

      const rootRank = {};
      list.forEach(o => {
        const r = rootKey(o);
        const rank = Number(o.root_order_id || (!isChild(o) ? o.id : 0)) || 0;
        rootRank[r] = Math.max(rootRank[r] || 0, rank);
      });

      const sortedList = [...list].sort((a, b) => {
        const ra = rootKey(a);
        const rb = rootKey(b);

        if (ra !== rb) return (rootRank[rb] || 0) - (rootRank[ra] || 0);

        const aChild = isChild(a);
        const bChild = isChild(b);
        if (aChild !== bChild) return aChild ? 1 : -1;

        return (a.order_no || "").localeCompare(b.order_no || "", "tr", { numeric: true });
      });

      setOrders(sortedList);
      const counts = list.reduce((a, o) => { a[o.status] = (a[o.status] || 0) + 1; return a; }, {});
      setStats({ total: list.length, ...counts });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [w]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);


  const openTreeModal = async (order) => {
    setTreeModal(null);
    setTreeError("");
    setTreeLoading(true);

    try {
      const res = await fetch(`${API}/work-orders/${order.id}/tree`, { headers: headers() });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || w.treeLoadFailed);
      }

      const data = await res.json();
      setTreeModal(data);
    } catch (e) {
      setTreeError(e.message || w.connectionError);
      setTreeModal({});
    } finally {
      setTreeLoading(false);
    }
  };

  const STATUS_CFG  = getStatusCfg();
  const PRIORITY_CFG = getPriorityCfg();

  const filtered = orders.filter(o => {
    const matchSearch = !search ||
      (o.order_no || "").toLowerCase().includes(search.toLowerCase()) ||
      (o.product_name || "").toLowerCase().includes(search.toLowerCase()) ||
      (o.customer || "").toLowerCase().includes(search.toLowerCase());
    return matchSearch && (filterStatus === "all" || o.status === filterStatus) && (filterPriority === "all" || o.priority === filterPriority);
  });

  const pagedOrders = filtered.slice((page - 1) * pageSize, page * pageSize);

  const selectStyle = { padding: "10px 14px", background: t.bg.input, border: `1px solid ${t.border.input}`, borderRadius: 10, color: t.text.primary, fontSize: 13, outline: "none", cursor: "pointer" };

  return (
    <div style={{ minHeight: "100vh", background: t.bg.page, color: t.text.primary, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <style>{`@keyframes sf-spin { to{transform:rotate(360deg)} }`}</style>
      <div style={{ maxWidth: 1600, margin: "0 auto", padding: "28px 20px" }}>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 800, color: t.text.primary, margin: 0 }}>📋 {w.title}</h1>
            <p style={{ color: t.text.muted, margin: "6px 0 0", fontSize: 14 }}>{w.subtitle}</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={fetchOrders} style={{ padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>🔄 {w.refresh}</button>
            <button onClick={() => {
              const hdrs = [w.orderNo, w.customerL, w.productCode, w.qty, w.producedQty, w.statusLabel, w.priority_, w.machine_, w.operator_, w.createdAt];
              const rows = orders.map(o => [o.order_no, o.customer, o.product_code, o.quantity, o.produced_quantity ?? o.produced_qty ?? o.completed_quantity ?? 0, o.status, o.priority, o.machine || "—", o.assigned_to || "—", o.created_at ? new Date(o.created_at).toLocaleDateString(getLocale()) : "—"]);
              const csv = [hdrs, ...rows].map(r => r.map(v => `"${String(v || "").replace(/"/g, '""')}"`).join(";")).join("\n");
              const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url; a.download = `work_orders_${new Date().toISOString().slice(0, 10)}.csv`;
              a.click(); URL.revokeObjectURL(url);
            }} style={{ display: canView("btn_export_excel") ? undefined : "none", padding: "10px 16px", borderRadius: 10, border: `1px solid ${t.border.input}`, background: t.bg.toggle, color: t.text.secondary, cursor: "pointer", fontSize: 13 }}>📥 Excel</button>
            <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ display: canView("btn_new_order") ? undefined : "none", padding: "10px 20px", borderRadius: 10, border: "none", background: "linear-gradient(135deg,#3b82f6,#6366f1)", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
              {w.new}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          {[
            [w.statTotal,     stats.total       || 0, t.accent.blue,   "📋"],
            [w.inProduction,   stats.in_progress || 0, t.accent.yellow, "▶"],
            [w.completed, stats.completed   || 0, t.accent.green,  "✅"],
            [w.released,    stats.released    || 0, t.accent.purple, "🔓"],
          ].map(([label, value, color, icon]) => (
            <div key={label} style={{ flex: 1, minWidth: 140, background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, boxShadow: t.shadow.card }}>
              <span style={{ fontSize: 24 }}>{icon}</span>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
                <div style={{ fontSize: 12, color: t.text.muted, fontWeight: 600 }}>{label}</div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
          <input placeholder={`🔍 ${w.searchPlaceholder}`} value={search} onChange={e => setSearch(e.target.value)} style={{ ...selectStyle, flex: 1, minWidth: 220 }} />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
            <option value="all">{w.allStatuses}</option>
            {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={selectStyle}>
            <option value="all">{w.allPriorities}</option>
            {Object.entries(PRIORITY_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <div style={{ display: "flex", gap: 4, background: t.bg.toggle, border: `1px solid ${t.border.input}`, borderRadius: 10, padding: 4 }}>
            {[["grid", "⊞"], ["list", "≡"]].map(([v, icon]) => (
              <button key={v} onClick={() => { setView(v); localStorage.setItem("view_workorders", v); }} style={{ padding: "6px 12px", borderRadius: 7, border: "none", cursor: "pointer", background: view === v ? t.bg.toggleActive : "none", color: view === v ? t.text.primary : t.text.muted, fontSize: 16, fontWeight: 600 }}>{icon}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "80px 0", gap: 12 }}>
            <div style={{ width: 28, height: 28, border: `2px solid ${t.border.default}`, borderTopColor: t.accent.blue, borderRadius: "50%", animation: "sf-spin 0.8s linear infinite" }} />
            <span style={{ color: t.text.muted }}>{w.loading_}</span>
          </div>
        ) : error ? (
          <div style={{ padding: 32, textAlign: "center", background: t.accent.redBg, border: `1px solid ${t.accent.red}44`, borderRadius: 14, color: t.accent.red }}>
            ⚠️ {error}<br />
            <button onClick={fetchOrders} style={{ marginTop: 12, padding: "8px 16px", borderRadius: 8, border: `1px solid ${t.accent.red}44`, background: "none", color: t.accent.red, cursor: "pointer" }}>
              {w.retry}
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: "center", color: t.text.placeholder, padding: "80px 0" }}>
            {orders.length === 0 ? (w.noOrders) : (w.noMatch)}
          </div>
        ) : view === "grid" ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 16 }}>
            {pagedOrders.map(o => <WorkOrderCard key={o.id} order={o} onClick={setSelected} t={t} themeName={themeName} />)}
          </div>
        ) : (
          <div style={{ background: t.bg.card, border: `1px solid ${t.border.card}`, borderRadius: 14, overflowX: "auto", overflowY: "hidden", boxShadow: t.shadow.card }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "auto" }}>
              <thead>
                <tr style={{ background: t.bg.theadRow }}>
                  {[
                    w.level,
                    w.dependency,
                    ...(canView("col_approval") ? [w.approval] : []),
                    w.orderNo,
                    w.customerL,
                    w.product_,
                    w.qty,
                    w.producedQty,
                    w.statusLabel,
                    w.priority_,
                    w.deadline,
                    w.actions_,
                  ].map(h => (
                    <th key={h} style={{ padding: "11px 9px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.text.label, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pagedOrders.map((o, i) => (
                  <tr key={o.id} onClick={() => setSelected(o)} style={{ borderTop: `1px solid ${t.border.subtle}`, background: !o.parent_order_no ? "rgba(16,185,129,0.08)" : t.bg.metricCard, borderLeft: !o.parent_order_no ? `3px solid ${t.accent.green}` : `3px solid transparent` }}>
                    <td style={{ padding: "10px 9px", fontSize: 12, color: t.text.secondary, fontWeight: 700 }}>
                      L{o.level_no ?? 0}
                    </td>
                    <td style={{ padding: "10px 9px" }}>
                      <span style={{
                        padding: "3px 8px",
                        borderRadius: 8,
                        fontSize: 11,
                        fontWeight: 700,
                        color: o.dependency_status === "ready" ? "#10b981" : "#ef4444",
                        background: o.dependency_status === "ready" ? "rgba(16,185,129,0.12)" : "rgba(239,68,68,0.12)"
                      }}>
                        {depLabel(o.dependency_status)}
                      </span>
                    </td>
                    <td style={{
                      display: canView("col_approval") ? undefined : "none",
                      padding: "10px 9px"
                    }}>
                      <span style={{
                        padding: "3px 8px",
                        borderRadius: 8,
                        fontSize: 11,
                        fontWeight: 700,
                        color: o.approval_status === "approved" ? "#10b981" : o.approval_status === "rejected" ? "#ef4444" : "#f59e0b",
                        background: o.approval_status === "approved" ? "rgba(16,185,129,0.12)" : o.approval_status === "rejected" ? "rgba(239,68,68,0.12)" : "rgba(245,158,11,0.12)"
                      }}>
                        {approvalLabel(o.approval_status, o.status)}
                      </span>
                    </td>
                    <td style={{ padding: "10px 9px", fontWeight: !o.parent_order_no ? 800 : 700, color: !o.parent_order_no ? t.accent.green : t.accent.blue, fontSize: 12, whiteSpace: "nowrap" }}>
                      {o.parent_order_no && (
                        <span style={{ color: t.text.muted, marginRight: 4 }}>└─</span>
                      )}
                      {o.order_no || `#${o.id}`}
                    </td>
                    <td style={{ padding: "10px 9px", fontSize: 13, color: t.text.secondary }}>{o.customer || "—"}</td>
                    <td style={{ padding: "10px 9px", fontSize: 13, color: t.text.primary }}>{o.product_name || o.product_code || "—"}</td>
                    <td style={{ padding: "10px 9px", fontSize: 13, color: t.text.secondary }}>{o.quantity} {o.unit || w.pcs}</td>
                    <td style={{ padding: "10px 9px", fontSize: 13, color: t.accent.green, fontWeight: 600 }}>{o.produced_quantity ?? o.produced_qty ?? o.completed_quantity ?? 0} {o.unit || w.pcs}</td>
                    <td style={{ padding: "10px 9px" }}><Badge status={o.status} themeName={themeName} parentOrderNo={o.parent_order_no} /></td>
                    <td style={{ padding: "10px 9px" }}><PriorityBadge priority={o.priority} /></td>
                    <td style={{ padding: "10px 9px", fontSize: 12, color: t.text.muted }}>{fmt(o.planned_end || o.deadline)}</td>
                    <td style={{ padding: "10px 8px", whiteSpace: "nowrap" }}>
                      <div style={{ display: "flex", gap: 5, flexWrap: "nowrap" }}>
                        <button title={w.treeAction} onClick={(e) => { e.stopPropagation(); openTreeModal(o); }} style={{ padding: "5px 10px", borderRadius: 7, fontSize: 11, cursor: "pointer", background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.35)", color: "#10b981", fontWeight: 800, whiteSpace: "nowrap" }}>🌳 {w.treeAction}</button>
                        <button title={w.detailAction} onClick={(e) => { e.stopPropagation(); setSelected(o); }} style={{ padding: "5px 10px", borderRadius: 7, fontSize: 11, cursor: "pointer", background: t.accent.blueBg, border: `1px solid ${t.accent.blueBorder}`, color: t.accent.blue, fontWeight: 800, whiteSpace: "nowrap" }}>👁️ {w.detailAction}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && (
          <div style={{ marginTop: 16, color: t.text.placeholder, fontSize: 12, textAlign: "right" }}>
            {filtered.length} / {orders.length} {w.orderCount}
          </div>
        )}
      </div>

      {(treeModal || treeLoading || treeError) && (
        <WorkOrderTreeModal
          tree={treeModal}
          loading={treeLoading}
          error={treeError}
          t={t}
          themeName={themeName}
          onClose={() => { setTreeModal(null); setTreeError(""); setTreeLoading(false); }}
        />
      )}

      {!loading && !error && filtered.length > 0 && (
        <PaginationBar
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalRecords={filtered.length}
          label={w.recordUnit}
          t={t}
        />
      )}

      {selected && (
        <WorkOrderDetailModal order={selected} t={t} themeName={themeName} onClose={() => setSelected(null)}
          onEdit={(o) => { setEditing(o); setShowForm(true); setSelected(null); }}
          onRefresh={() => { fetchOrders(); setSelected(null); }} />
      )}
      {showForm && (
        <WorkOrderFormModal order={editing} users={users}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); fetchOrders(); }} />
      )}
    </div>
  );
}
